// Contract: Idempotent global chrome behavior for JS state, announcement dismissal, mobile menu, search modal controls, and Shopify section lifecycle resets.
(() => {
if (window.HeritageGlobalChromeInitialized) return;
window.HeritageGlobalChromeInitialized = true;

const setRootClassSafely = (className, isApplied) => {
  try {
    const classList = document.documentElement?.classList;
    if (!classList || typeof classList.toggle !== 'function') return false;
    classList.toggle(className, Boolean(isApplied));
    return true;
  } catch (error) {
    return false;
  }
};

setRootClassSafely('js', true);
let lastSearchTrigger = null;
const announcementStoragePrefix = 'hm:announcement-dismissed:';
const storefrontRequestEventsApi = window.HeritageStorefrontRequestEvents;
const dispatchOverlayCoordinationEvent = typeof storefrontRequestEventsApi?.dispatchDocumentEvent === 'function'
  ? storefrontRequestEventsApi.dispatchDocumentEvent
  : () => false;
const getScopedElements = (root, selector) => {
  const elements = [];
  try {
    if (root?.matches?.(selector)) elements.push(root);
    root?.querySelectorAll?.(selector).forEach((element) => elements.push(element));
  } catch (error) {
    // A partial Shopify section root contributes no chrome elements.
  }
  return elements;
};

const getEventTargetClosest = (target, selector) => {
  try { return target?.matches?.(selector) ? target : target?.closest?.(selector) || null; }
  catch (error) { return null; }
};
const getGlobalChromeEventTarget = (event) => { try { return event?.target || null; } catch (error) { return null; } };
const getGlobalChromeEventKey = (event) => { try { return event?.key || ''; } catch (error) { return ''; } };
const getGlobalChromeDatasetValue = (target, key) => {
  try { return target?.dataset?.[key] || ''; }
  catch (error) { return ''; }
};
const getGlobalChromeDocumentElement = (selector) => { try { return { element: document.querySelector?.(selector) || null, readable: true }; } catch (error) { return { element: null, readable: false }; } };
const getGlobalChromeDocumentElements = (selector) => { try { return Array.from(document.querySelectorAll?.(selector) || []); } catch (error) { return []; } };
const canFocusSafely = (target) => { try { return Boolean(target?.isConnected && typeof target.focus === 'function'); } catch (error) { return false; } };
const focusSafely = (target) => {
  try {
    if (canFocusSafely(target)) target.focus();
  } catch (error) {
    // Focus restoration is progressive enhancement; the settled modal state remains usable.
  }
};
const scheduleFocus = (target) => {
  if (!canFocusSafely(target)) return;

  try {
    if (typeof requestAnimationFrame === 'function') {
      requestAnimationFrame(() => focusSafely(target));
      return;
    }
  } catch (error) {
    // A partial browser/runtime scheduler must not interrupt search or menu state.
  }
  focusSafely(target);
};

const searchModalFocusableSelector = [
  'a[href]:not([tabindex="-1"])',
  'button:not([disabled]):not([tabindex="-1"]):not(.search-modal__backdrop)',
  'input:not([disabled]):not([tabindex="-1"])',
  'select:not([disabled]):not([tabindex="-1"])',
  'textarea:not([disabled]):not([tabindex="-1"])',
].join(',');

const getSearchModalFocusableElements = (modal) => {
  try {
    return Array.from(modal?.querySelectorAll?.(searchModalFocusableSelector) || [])
      .filter((element) => !element.closest?.('[hidden]') && element.getAttribute?.('aria-hidden') !== 'true');
  } catch (error) {
    return [];
  }
};
const trapSearchModalFocus = (event, modal) => {
  const focusableElements = getSearchModalFocusableElements(modal);
  if (focusableElements.length === 0) return false;

  const firstFocusable = focusableElements[0];
  const lastFocusable = focusableElements[focusableElements.length - 1];
  let activeElement = null;
  let isBackward = false;

  try {
    activeElement = document.activeElement;
    isBackward = Boolean(event?.shiftKey);
  } catch (error) {
    activeElement = null;
  }

  const shouldWrapBackward = isBackward && (activeElement === firstFocusable || !modal.contains?.(activeElement));
  const shouldWrapForward = !isBackward && (activeElement === lastFocusable || !modal.contains?.(activeElement));
  if (!shouldWrapBackward && !shouldWrapForward) return false;

  try {
    event?.preventDefault?.();
  } catch (error) {
    // Focus containment still proceeds when a partial event cannot be cancelled.
  }
  focusSafely(shouldWrapBackward ? lastFocusable : firstFocusable);
  return true;
};

const setSearchModalVisibilitySafely = (modal, isOpen) => {
  try {
    if (!modal) return false;
    modal.hidden = !isOpen;
    return true;
  } catch (error) {
    return false;
  }
};

const setTemplateModalVisibilitySafely = (modal, isOpen) => {
  try {
    if (!modal) return false;
    modal.hidden = !isOpen;
    return true;
  } catch (error) {
    return false;
  }
};

const setExpandedStateSafely = (trigger, expanded) => {
  try {
    if (!trigger || typeof trigger.setAttribute !== 'function') return false;
    trigger.setAttribute('aria-expanded', String(expanded));
    return true;
  } catch (error) {
    return false;
  }
};

const getAnnouncementStorageKey = (bar) => {
  const announcementId = getGlobalChromeDatasetValue(bar, 'announcementId') || 'global';
  return `${announcementStoragePrefix}${announcementId}`;
};

const isAnnouncementDismissed = (bar) => {
  try {
    return window.sessionStorage.getItem(getAnnouncementStorageKey(bar)) === 'true';
  } catch (error) {
    return false;
  }
};

const setAnnouncementVisibilitySafely = (bar, isVisible) => {
  try {
    if (!bar) return false;
    bar.hidden = !isVisible;
    return true;
  } catch (error) {
    return false;
  }
};

const dismissAnnouncement = (bar) => {
  if (!setAnnouncementVisibilitySafely(bar, false)) return;

  try {
    window.sessionStorage.setItem(getAnnouncementStorageKey(bar), 'true');
  } catch (error) {
    // Storage can be blocked in private modes; the in-page dismissal still works.
  }
};

const initializeAnnouncementBars = (root = document) => {
  getScopedElements(root, '[data-announcement-bar]').forEach((bar) => {
    if (isAnnouncementDismissed(bar)) {
      setAnnouncementVisibilitySafely(bar, false);
    }
  });
};

const setMobileMenuVisibilitySafely = (menu, isOpen) => {
  try {
    if (!menu) return false;
    menu.hidden = !isOpen;
    return true;
  } catch (error) {
    return false;
  }
};

const syncMobileMenuToggleStateSafely = (toggle, isOpen) => {
  try {
    if (!toggle || typeof toggle.setAttribute !== 'function') return;
    const currentLabel = toggle.getAttribute('aria-label') || '';
    const openLabel = getGlobalChromeDatasetValue(toggle, 'menuLabelOpen') || currentLabel;
    const closeLabel = getGlobalChromeDatasetValue(toggle, 'menuLabelClose') || currentLabel;
    toggle.setAttribute('aria-expanded', String(isOpen));
    toggle.setAttribute('aria-label', isOpen ? closeLabel : openLabel);
  } catch (error) {
    // Drawer visibility remains authoritative when an optional ARIA/label host is partial.
  }
};

const readMobileMenuStateSafely = (toggle) => {
  try {
    if (!toggle || typeof toggle.getAttribute !== 'function') return null;
    const menuId = toggle.getAttribute('aria-controls');
    return {
      isExpanded: toggle.getAttribute('aria-expanded') === 'true',
      menu: menuId ? document.getElementById(menuId) : null,
    };
  } catch (error) {
    return null;
  }
};

const resetMobileMenuBranches = (menu) => {
  try {
    menu?.querySelectorAll?.('details').forEach((branch) => {
      try {
        branch.open = false;
      } catch (error) {
        // Collapsed submenus are progressive cleanup after the drawer itself is hidden.
      }
    });
  } catch (error) {
    // A partial drawer host cannot interrupt its already-settled closed state.
  }
};

const setMobileMenuState = (toggle, menu, expanded) => {
  const shouldExpand = Boolean(expanded && menu);
  if (menu && !setMobileMenuVisibilitySafely(menu, shouldExpand)) return false;

  setRootClassSafely('mobile-menu-open', shouldExpand);
  syncMobileMenuToggleStateSafely(toggle, shouldExpand);

  if (!shouldExpand) {
    resetMobileMenuBranches(menu);
  }

  return true;
};

const encodeSearchTerm = (term) => encodeURIComponent(term).replace(/%20/g, '+');

const submitSearchSortForm = (form) => {
  if (!form) return;

  try {
    if (typeof form.submit === 'function') {
      form.submit();
      return;
    }
  } catch (error) {
    // A form integration can shadow or replace submit; preserve the native GET fallback below.
  }

  try {
    form.requestSubmit?.();
  } catch (error) {
    // Search sorting is progressive enhancement; a partial form API must not throw inside the shared change delegate.
  }
};

const getScopedElement = (root, selector) => {
  try {
    return root?.querySelector?.(selector) || null;
  } catch (error) {
    return null;
  }
};

const setSearchPreviewVisibilitySafely = (element, isVisible) => {
  try {
    if (!element) return false;
    element.hidden = !isVisible;
    return true;
  } catch (error) {
    return false;
  }
};

const setSearchPreviewTextSafely = (element, text) => {
  try {
    if (!element) return false;
    element.textContent = text;
    return true;
  } catch (error) {
    return false;
  }
};

const setSearchPreviewDestinationSafely = (link, destination) => {
  try {
    if (!link || typeof link.setAttribute !== 'function') return false;
    link.setAttribute('href', destination);
    return true;
  } catch (error) {
    return false;
  }
};

const updateSearchPreview = (input) => {
  let modal = null;

  try {
    modal = input?.closest?.('[data-search-modal]') || null;
  } catch (error) {
    return;
  }

  if (!modal) return;

  let term = '';

  try {
    term = typeof input.value === 'string' ? input.value.trim() : '';
  } catch (error) {
    return;
  }

  setSearchPreviewVisibilitySafely(getScopedElement(modal, '[data-search-dynamic]'), Boolean(term));
  setSearchPreviewTextSafely(getScopedElement(modal, '[data-search-query-label]'), term);

  if (term) {
    setSearchPreviewDestinationSafely(
      getScopedElement(modal, '[data-search-submit-link]'),
      `/search?q=${encodeSearchTerm(term)}&options%5Bprefix%5D=last`,
    );
  }
};

const closeSearchModal = ({ restoreFocus = true } = {}) => {
  const { element: modal, readable } = getGlobalChromeDocumentElement('[data-search-modal]:not([hidden])');
  const focusReturnTarget = lastSearchTrigger;

  if (!readable) return false;
  if (modal && !setSearchModalVisibilitySafely(modal, false)) return false;
  setRootClassSafely('search-modal-open', false);
  getGlobalChromeDocumentElements('[data-search-open][aria-expanded="true"]').forEach((trigger) => {
    setExpandedStateSafely(trigger, false);
  });
  lastSearchTrigger = null;

  if (restoreFocus) scheduleFocus(focusReturnTarget);
  return true;
};

const resetTemplateRouterDomState = () => {
  let didCloseTemplateModals = true;

  getScopedElements(document, '[data-template-router]').forEach((router) => {
    const modal = getScopedElement(router, '[data-template-modal]');
    if (!modal || modal.hidden) return;

    if (!setTemplateModalVisibilitySafely(modal, false)) {
      didCloseTemplateModals = false;
      return;
    }

    try {
      router.querySelectorAll('[data-template-card][aria-expanded="true"]').forEach((trigger) => {
        setExpandedStateSafely(trigger, false);
      });
    } catch (error) {
      // The modal visibility commit remains authoritative if a partial router cannot expose trigger cleanup.
    }
  });
  const templateModalState = hasOpenTemplateModal();
  if (templateModalState === null) return false;
  setRootClassSafely('template-modal-open', templateModalState);
  return didCloseTemplateModals;
};

const resetTemplateRouterState = () => {
  try {
    window.HeritageTemplateRouterEvents?.reset?.();
  } catch (error) {
    // Overlay coordination is progressive enhancement; search still opens when an optional router facade fails.
  }

  // A late integration can replace the public facade with a silent no-op. Reconcile the
  // visible overlay state after the optional facade so search never opens over a template modal.
  return resetTemplateRouterDomState();
};

const hasOpenTemplateModal = () => { const { element, readable } = getGlobalChromeDocumentElement('[data-template-modal]:not([hidden])'); return readable ? Boolean(element) : null; };

const getSearchModalFromTriggerSafely = (trigger) => {
  try {
    if (!trigger || typeof trigger.getAttribute !== 'function') return null;
    const modalId = trigger.getAttribute('aria-controls');
    return modalId ? document.getElementById?.(modalId) || null : document.querySelector?.('[data-search-modal]') || null;
  } catch (error) {
    return null;
  }
};

const openSearchModal = (trigger) => {
  const modal = getSearchModalFromTriggerSafely(trigger);
  if (!modal) return;

  if (!dispatchOverlayCoordinationEvent('heritage:search-modal-opening')) {
    if (!resetTemplateRouterState()) return;
  }
  if (hasOpenTemplateModal() !== false) return;

  const { element: openToggle } = getGlobalChromeDocumentElement('[data-menu-toggle][aria-expanded="true"]');
  if (openToggle) {
    const mobileMenuState = readMobileMenuStateSafely(openToggle);
    if (!mobileMenuState || !setMobileMenuState(openToggle, mobileMenuState.menu, false)) return;
  }

  if (!setSearchModalVisibilitySafely(modal, true)) return;
  setRootClassSafely('search-modal-open', true);
  setExpandedStateSafely(trigger, true);
  lastSearchTrigger = trigger;

  const input = getScopedElement(modal, '[data-search-input]');
  if (input) {
    updateSearchPreview(input);
    scheduleFocus(input);
  }
};

const resetHeaderSectionState = (root) => {
  const headers = getScopedElements(root, '[data-site-header]');
  if (headers.length === 0) return;

  let didCloseMobileMenus = true;

  headers.forEach((header) => {
    getScopedElements(header, '[data-menu-toggle]').forEach((toggle) => {
      const mobileMenuState = readMobileMenuStateSafely(toggle);
      if (!mobileMenuState || !setMobileMenuState(toggle, mobileMenuState.menu, false)) didCloseMobileMenus = false;
    });
  });

  const didCloseSearchModal = closeSearchModal({ restoreFocus: false });
  if (didCloseMobileMenus) setRootClassSafely('mobile-menu-open', false);
  if (didCloseSearchModal) setRootClassSafely('search-modal-open', false);
};

document.addEventListener('click', (event) => {
  const target = getGlobalChromeEventTarget(event);
  const announcementClose = getEventTargetClosest(target, '[data-announcement-close]');
  if (announcementClose) {
    const announcementBar = getEventTargetClosest(announcementClose, '[data-announcement-bar]');
    if (announcementBar) {
      dismissAnnouncement(announcementBar);
    }
    return;
  }

  const searchOpen = getEventTargetClosest(target, '[data-search-open]');

  if (searchOpen) {
    openSearchModal(searchOpen);
    return;
  }

  if (getEventTargetClosest(target, '[data-search-close]')) {
    closeSearchModal();
    return;
  }

  const toggle = getEventTargetClosest(target, '[data-menu-toggle]');

  if (toggle) {
    const mobileMenuState = readMobileMenuStateSafely(toggle);
    if (!mobileMenuState) return;
    const shouldOpenMenu = !mobileMenuState.isExpanded && Boolean(mobileMenuState.menu);
    if (shouldOpenMenu && !closeSearchModal({ restoreFocus: false })) return;
    setMobileMenuState(toggle, mobileMenuState.menu, shouldOpenMenu);
    return;
  }

  const { element: openToggle } = getGlobalChromeDocumentElement('[data-menu-toggle][aria-expanded="true"]');
  const header = getEventTargetClosest(target, '[data-site-header]');
  if (!openToggle || header) return;

  const mobileMenuState = readMobileMenuStateSafely(openToggle);
  if (!mobileMenuState) return;
  setMobileMenuState(openToggle, mobileMenuState.menu, false);
});

document.addEventListener('input', (event) => {
  const searchInput = getEventTargetClosest(getGlobalChromeEventTarget(event), '[data-search-input]');
  if (!searchInput) return;

  updateSearchPreview(searchInput);
});

document.addEventListener('change', (event) => {
  const searchSort = getEventTargetClosest(getGlobalChromeEventTarget(event), '[data-search-sort]');
  if (!searchSort) return;

  submitSearchSortForm(searchSort.form);
});

document.addEventListener('keydown', (event) => {
  const key = getGlobalChromeEventKey(event);
  if (key === 'Tab') {
    const { element: searchModal } = getGlobalChromeDocumentElement('[data-search-modal]:not([hidden])');
    if (searchModal && trapSearchModalFocus(event, searchModal)) return;
  }
  if (key === 'Escape') {
    const { element: searchModal } = getGlobalChromeDocumentElement('[data-search-modal]:not([hidden])');
    if (searchModal) {
      closeSearchModal();
      return;
    }

  }

  if (key !== 'Escape') return;

  const { element: openToggle } = getGlobalChromeDocumentElement('[data-menu-toggle][aria-expanded="true"]');
  if (!openToggle) return;

  const mobileMenuState = readMobileMenuStateSafely(openToggle);
  if (mobileMenuState && setMobileMenuState(openToggle, mobileMenuState.menu, false)) focusSafely(openToggle);
});

document.addEventListener('heritage:template-modal-opening', () => {
  closeSearchModal({ restoreFocus: false });
});

window.HeritageGlobalChromeEvents = {
  closeSearchModal: () => closeSearchModal({ restoreFocus: false }),
};

document.addEventListener('shopify:section:load', (event) => {
  const target = getGlobalChromeEventTarget(event);
  initializeAnnouncementBars(target);
  resetHeaderSectionState(target);
});

document.addEventListener('shopify:section:unload', (event) => {
  resetHeaderSectionState(getGlobalChromeEventTarget(event));
});

initializeAnnouncementBars();
})();
